/**
 * Integrations Modal Component
 * Unified searchable interface for all integrations
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/features/auth/hooks/useSession';
import { useConversation } from '@/app/providers/ConversationContext';
import { apiFetch } from '@/lib/apiClient';
import { connectProvider } from '@/features/oauth/lib/connect-provider';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { useIsCompactLayout } from '@/common/hooks';
import { IntegrationSyncStatusBadge } from '@/common/components/IntegrationSyncStatusBadge';
import {
  listIntegrations,
  deleteIntegration,
  authorizeIntegration,
  syncIntegration,
  listProviders,
  createIntegration,
  getWebSearchStatus,
  enableWebSearch,
  disableWebSearch,
} from '@/lib/api/endpoints/integrations';
import type { Integration, ProviderPreset, ProviderCategory, CreateIntegrationRequest } from '@/lib/api/endpoints/integrations';
import './IntegrationsModal.css';
import { emailApiPath } from '@/lib/email-api'
import {
  GoogleLogo,
  MicrosoftLogo,
  ShopifyLogo,
  PROVIDER_LOGOS,
} from './integrations/ProviderLogos';
import { CATEGORY_LABELS, CATEGORY_ORDER, DISPLAY_ORDER } from './integrations/constants';
import type {
  IntegrationsModalProps,
  CalendarConnection,
  EmailStatus,
  UnifiedIntegration,
  GoogleAccountRow,
} from './integrations/types';
import { SyncExplanationModal } from './integrations/SyncExplanationModal';

export function IntegrationsModal({ isOpen, onClose }: IntegrationsModalProps) {
  useEscapeKey(onClose, isOpen);
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30);
  const isMobile = useIsCompactLayout();
  const { token } = useSession();
  const { activeConversation } = useConversation();
  const queryClient = useQueryClient();

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ProviderCategory | 'all'>('all');

  // Built-in integrations state
  const [calendarStatus, setCalendarStatus] = useState<CalendarConnection | null>(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null);

  // Generic integrations and providers
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [providers, setProviders] = useState<ProviderPreset[]>([]);

  // Web search enabled state (from database)
  const [webSearchEnabled, setWebSearchEnabled] = useState(true); // Default to enabled

  // Expanded state for configuration
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Operation states
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Google accounts list state
  const [googleAccounts, setGoogleAccounts] = useState<GoogleAccountRow[]>([]);
  const [loadingGoogleAccounts, setLoadingGoogleAccounts] = useState(false);

  // Sync explanation modal (shown before Google OAuth)
  const [showSyncExplanationModal, setShowSyncExplanationModal] = useState(false);

  // OAuth/API key integration setup
  const [setupName, setSetupName] = useState('');
  const [setupApiKey, setSetupApiKey] = useState('');
  const [setupClientId, setSetupClientId] = useState('');
  const [setupClientSecret, setSetupClientSecret] = useState('');
  const [shopifyDomain, setShopifyDomain] = useState('');

  // Find event_list module in the active conversation
  const eventListModule = activeConversation?.modules?.find(m => m.type === 'event_list');
  const moduleId = eventListModule?.id;
  const scopeQuery = '';

  // Check calendar connection status (checks for ANY Google accounts at workspace level)
  const checkCalendarStatus = useCallback(async () => {
    if (!token) return;
    setCalendarLoading(true);
    try {
      // Use the accounts endpoint to check if ANY Google accounts exist (workspace-scoped after migration 044)
      const data = await apiFetch<{ accounts: any[] }>(
        `/integrations/google/accounts${scopeQuery}`,
        { authToken: token }
      );
      // Connected if we have at least one account
      setCalendarStatus({ connected: data.accounts.length > 0 });
    } catch {
      setCalendarStatus({ connected: false });
    } finally {
      setCalendarLoading(false);
    }
  }, [token, scopeQuery]);

  // Check Gmail status
  const checkEmailStatus = useCallback(async () => {
    if (!token) return;
    try {
      const data = await apiFetch<EmailStatus>(emailApiPath() + '/status', { authToken: token });
      setEmailStatus(data);
    } catch {
      setEmailStatus({ connected: false, account: null });
    }
  }, [token]);

  // Load generic integrations
  const loadIntegrations = useCallback(async () => {
    if (!token) return;
    try {
      const items = await listIntegrations(token);
      setIntegrations(items);
    } catch {
      setIntegrations([]);
    }
  }, [token]);

  // Load providers
  const loadProviders = useCallback(async () => {
    if (!token) return;
    try {
      const items = await listProviders(token);
      setProviders(items);
    } catch {
      setProviders([]);
    }
  }, [token]);

  // Check web search status
  const checkWebSearchStatus = useCallback(async () => {
    if (!token) return;
    try {
      const enabled = await getWebSearchStatus(token);
      setWebSearchEnabled(enabled);
    } catch {
      setWebSearchEnabled(true); // Default to enabled on error
    }
  }, [token]);

  // Load all data on open
  useEffect(() => {
    if (isOpen) {
      checkCalendarStatus();
      checkEmailStatus();
      loadIntegrations();
      loadProviders();
      checkWebSearchStatus();
      setSearchQuery('');
      setSelectedCategory('all');
      setExpandedId(null);
      setError(null);
      setSuccessMsg(null);
      setShopifyDomain('');
    }
  }, [isOpen, checkCalendarStatus, checkEmailStatus, loadIntegrations, loadProviders, checkWebSearchStatus]);

  // (The popup-era 'integration_oauth_complete' storage listener was removed with
  // the OAuth popup. Connect is a same-window redirect now: the param handler
  // below refreshes when the modal is open on return, and App.tsx's OAuth-return
  // handler fires the centralized refetch regardless.)

  // Handle calendar OAuth redirect
  useEffect(() => {
    if (!isOpen) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected') === 'true') {
      const url = new URL(window.location.href);
      url.searchParams.delete('calendar_connected');
      window.history.replaceState({}, '', url.toString());

      // Refresh calendar status to detect the new floating integration
      checkCalendarStatus();
    }
    if (params.get('integration_connected') === 'true') {
      const url = new URL(window.location.href);
      url.searchParams.delete('integration_connected');
      window.history.replaceState({}, '', url.toString());
      loadIntegrations();
    }
  }, [isOpen, checkCalendarStatus, loadIntegrations]);

  // Close on hamburger menu
  useEffect(() => {
    if (!isOpen) return;
    const handleCloseAllModals = () => onClose();
    window.addEventListener('closeAllModals', handleCloseAllModals);
    return () => window.removeEventListener('closeAllModals', handleCloseAllModals);
  }, [isOpen, onClose]);

  const msIntegration = useMemo(
    () => integrations.find(i => i.provider === 'microsoft' && i.status === 'active') ?? null,
    [integrations],
  );

  const shopifyProvider = useMemo(
    () => providers.find(p => p.id === 'shopify') ?? null,
    [providers],
  );
  const shopifyIntegration = useMemo(
    () => integrations.find(i => i.provider === 'shopify' && i.status === 'active') ?? null,
    [integrations],
  );

  // Build unified integrations list
  const unifiedIntegrations = useMemo((): UnifiedIntegration[] => {
    const items: UnifiedIntegration[] = [];

    // Web Search is always enabled in the background, not shown in UI

    // Google - built-in (always shown as "added" since it's a built-in)
    items.push({
      id: 'google',
      providerId: 'google',
      name: 'Google',
      description: 'Calendar, Gmail, and Contacts',
      category: 'productivity',
      isAdded: true,
      isBuiltIn: true,
      status: calendarStatus?.connected ? 'connected' : 'available',
      logo: GoogleLogo,
    });

    // Microsoft 365 - shown as built-in when server has OAuth credentials configured
    const msProvider = providers.find(p => p.id === 'microsoft');
    if (msProvider?.hasServerCredentials) {
      items.push({
        id: 'microsoft',
        providerId: 'microsoft',
        name: 'Microsoft 365',
        description: 'Outlook, Calendar, OneDrive, and OneNote',
        category: 'productivity',
        isAdded: true,
        isBuiltIn: true,
        status: msIntegration ? 'connected' : 'available',
        logo: MicrosoftLogo,
      });
    }

    // Shopify — shown as built-in when server has OAuth credentials
    if (shopifyProvider?.hasServerCredentials) {
      items.push({
        id: 'shopify',
        providerId: 'shopify',
        name: 'Shopify',
        description: 'E-commerce orders, products, and customers',
        category: 'business',
        isAdded: true,
        isBuiltIn: true,
        status: shopifyIntegration ? 'connected' : 'available',
        logo: ShopifyLogo,
      });
    }

    // Generic integrations (only active/connected ones)
    // Skip built-in providers (web_search) as they're handled separately
    for (const intg of integrations) {
      // Skip built-in providers — they're handled separately above
      if (intg.provider === 'web_search' || intg.provider === 'google' || intg.provider === 'microsoft' || intg.provider === 'shopify') continue;
      // Skip non-active integrations — they'll show as available for fresh add
      if (intg.status !== 'active') continue;

      const providerInfo = providers.find(p => p.id === intg.provider);
      items.push({
        id: intg.id,
        providerId: intg.provider,
        // Use provider name instead of stored name to avoid label mismatches
        name: providerInfo?.name || intg.name,
        description: providerInfo?.description || '',
        category: (providerInfo?.category || 'custom') as ProviderCategory,
        isAdded: true,
        isBuiltIn: false,
        status: 'connected',
        logo: PROVIDER_LOGOS[intg.provider],
        integration: intg,
      });
    }

    // Available providers (not yet connected)
    const connectedProviderIds = new Set([
      'google', 'microsoft', 'web_search', // Built-ins handled separately
      ...(shopifyProvider?.hasServerCredentials ? ['shopify'] : []),
      ...integrations.filter(i => i.status === 'active').map(i => i.provider),
    ]);
    for (const p of providers) {
      // Skip if already connected or is a built-in
      if (connectedProviderIds.has(p.id)) continue;

      items.push({
        id: `provider_${p.id}`,
        providerId: p.id,
        name: p.name,
        description: p.description || '',
        category: p.category as ProviderCategory,
        isAdded: false, // Not yet added
        isBuiltIn: false,
        status: 'available',
        logo: PROVIDER_LOGOS[p.id],
      });
    }

    return items;
  }, [webSearchEnabled, calendarStatus, integrations, providers, moduleId]);

  // Filter and sort integrations
  const filteredIntegrations = useMemo(() => {
    let items = unifiedIntegrations;

    // Filter by category
    if (selectedCategory !== 'all') {
      items = items.filter(i => i.category === selectedCategory);
    }

    // Filter by search query (also matches category names)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(i => {
        const matchesName = (i.name || '').toLowerCase().includes(query);
        const matchesDescription = (i.description || '').toLowerCase().includes(query);
        const matchesCategory = (i.category || '').toLowerCase().includes(query);
        return matchesName || matchesDescription || matchesCategory;
      });
    }

    // Sort by display order, then by status (connected first)
    return items.sort((a, b) => {
      // First sort by display order
      const aOrder = DISPLAY_ORDER.indexOf(a.providerId);
      const bOrder = DISPLAY_ORDER.indexOf(b.providerId);
      const aIndex = aOrder === -1 ? 999 : aOrder;
      const bIndex = bOrder === -1 ? 999 : bOrder;

      if (aIndex !== bIndex) return aIndex - bIndex;

      // Same display order position: connected first, then available
      const statusOrder = { connected: 0, available: 1 };
      const aStatus = statusOrder[a.status] ?? 3;
      const bStatus = statusOrder[b.status] ?? 3;
      if (aStatus !== bStatus) return aStatus - bStatus;

      // Same status: alphabetical
      return a.name.localeCompare(b.name);
    });
  }, [unifiedIntegrations, selectedCategory, searchQuery]);

  // Get available categories
  const availableCategories = useMemo(() => {
    const cats = new Set<ProviderCategory>();
    unifiedIntegrations.forEach(i => cats.add(i.category));
    return CATEGORY_ORDER.filter(c => c === 'all' || cats.has(c as ProviderCategory));
  }, [unifiedIntegrations]);

  // Action handlers
  const handleConnectGoogle = () => {
    setShowSyncExplanationModal(true);
  };

  const handleConnectShopify = async (domain: string) => {
    if (!token || !domain.trim()) return;
    setError(null);
    const cleanDomain = domain.trim().replace('.myshopify.com', '').replace(/^https?:\/\//, '');
    try {
      // Same-window redirect via the one connect helper. On return the modal
      // re-reads integrations (App.tsx fires the centralized refetch).
      await connectProvider({
        provider: 'shopify',
        authToken: token,
        shopDomain: cleanDomain,
        conversationId: activeConversation?.id,
      });
    } catch {
      setError('Failed to connect Shopify. Please try again.');
    }
  };

  const handleConnectMicrosoft = async () => {
    if (!token) return;
    setError(null);
    try {
      // No integrationId → FLOW A (enroll a new account): the backend keeps
      // prompt=select_account so the account picker shows. On return the modal
      // re-reads integrations (App.tsx fires the centralized refetch).
      await connectProvider({ provider: 'microsoft', authToken: token, conversationId: activeConversation?.id });
    } catch {
      setError('Failed to connect Microsoft. Please try again.');
    }
  };

  const handleConnectGoogleConfirmed = async () => {
    if (!token) return;

    // Conversation scope is required for module-level calendar linking.
    if (!activeConversation?.id) {
      setError('No active conversation selected. Please select a conversation first.');
      return;
    }

    setError(null);

    try {
      // No integrationId → FLOW A (enroll/reconnect a Google account): picker shows.
      await connectProvider({ provider: 'google', authToken: token, conversationId: activeConversation.id });
    } catch {
      setError('Failed to connect. Please try again.');
    }
  };

  const handleSyncGoogle = async () => {
    if (!token || !moduleId) return;
    setSyncing(true);
    setError(null);
    try {
      // Start async sync - returns immediately with syncId
      const startResponse = await apiFetch<{ syncId: string; status: string }>('/integrations/calendar/sync', {
        method: 'POST',
        authToken: token,
        body: JSON.stringify({ moduleId }),
      });
      const { syncId } = startResponse;

      // Poll for sync completion
      const maxPolls = 120; // 2 minutes max
      const pollInterval = 1000; // 1 second
      let polls = 0;

      while (polls < maxPolls) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        polls++;

        const statusResponse = await apiFetch<{ syncId: string; status: string; result?: any; error?: string }>(
          `/integrations/calendar/sync/${syncId}/status`,
          { authToken: token }
        );

        if (statusResponse.status === 'completed') {
          break;
        } else if (statusResponse.status === 'failed') {
          throw new Error(statusResponse.error || 'Sync failed');
        }
      }

      setSuccessMsg('Calendar synced!');
      setTimeout(() => setSuccessMsg(null), 3000);
      await checkCalendarStatus();
    } catch {
      setError('Failed to sync. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const fetchGoogleAccounts = useCallback(async () => {
    if (!token) return;
    setLoadingGoogleAccounts(true);
    try {
      const data = await apiFetch<{ accounts: GoogleAccountRow[] }>(
        `/integrations/google/accounts${scopeQuery}`,
        { authToken: token }
      );
      setGoogleAccounts(data.accounts);
    } catch (err) {
      console.error('Failed to fetch Google accounts:', err);
      setGoogleAccounts([]);
    } finally {
      setLoadingGoogleAccounts(false);
    }
  }, [token, scopeQuery]);

  const handleDisconnectGoogleAccount = async (accountId: string) => {
    if (!token) return;

    setDisconnecting(true);

    try {
      await apiFetch(`/integrations/google/${accountId}`, {
        method: 'DELETE',
        authToken: token,
      });

      // Notify every module that observes Google connection state to wipe
      // cached data for the disconnected account. Without this dispatch, the
      // events list / Gmail panel / Drive browser kept stale rows visible
      // until the next manual refresh (#2722, #2714).
      window.dispatchEvent(new CustomEvent('integrationAccountDisconnected', { detail: { accountId } }));
      // Invalidate React Query caches that mirror the connection state.
      // Includes `drive-accounts` to match IntegrationAccountsModal (#2722) — the
      // Drive tab strip is keyed on this query and would otherwise show a
      // stale account chip until the next refetch.
      queryClient.invalidateQueries({ queryKey: ['google-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['gdriveBrowse', 'moduleEnabled'] });
      queryClient.invalidateQueries({ queryKey: ['drive-accounts'] });

      // Refresh account list and status
      await fetchGoogleAccounts();
      await checkCalendarStatus();

      // Success message removed
      checkEmailStatus();
    } catch (err) {
      console.error('Failed to disconnect Google account:', err);
      setError('Failed to disconnect Google account.');
    } finally {
      setDisconnecting(false);
    }
  };

  // Fetch Google accounts when Google section is expanded
  useEffect(() => {
    if (expandedId === 'google') {
      fetchGoogleAccounts();
    }
  }, [expandedId, fetchGoogleAccounts]);

  const handleDeleteIntegration = async (id: string) => {
    if (!token) return;
    await deleteIntegration(token, id);
    setSuccessMsg('Integration disconnected.');
    setTimeout(() => setSuccessMsg(null), 3000);
    await loadIntegrations();
  };

  const handleToggleWebSearch = async (enable: boolean) => {
    if (!token) return;
    setError(null);

    // Optimistically update UI immediately
    setWebSearchEnabled(enable);

    try {
      if (enable) {
        await enableWebSearch(token);
      } else {
        await disableWebSearch(token);
      }
    } catch (err) {
      console.error('Failed to toggle web search:', err);
      // Revert on error
      setWebSearchEnabled(!enable);
      setError('Failed to update web search setting.');
    }
  };

  const handleConnectProvider = async (providerId: string, provider: ProviderPreset | undefined) => {
    if (!token || !provider) return;
    setSaving(true);
    setError(null);
    try {
      const payload: CreateIntegrationRequest = {
        provider: providerId,
        name: setupName.trim() || `My ${provider.name}`,
      };

      if (provider.supportsOAuth) {
        // For OAuth providers with server credentials, no user input needed
        if (provider.hasServerCredentials) {
          // Server will use its own credentials - just create and authorize
        } else {
          // For OAuth providers without server credentials, require client credentials from user
          if (!setupClientId.trim() || !setupClientSecret.trim()) {
            setError('Client ID and Client Secret are required.');
            setSaving(false);
            return;
          }
          payload.oauthConfig = {
            clientId: setupClientId.trim(),
            clientSecret: setupClientSecret.trim(),
          };
        }
      } else {
        // For API key providers
        if (!setupApiKey.trim()) {
          setError('API key is required.');
          setSaving(false);
          return;
        }
        payload.credentials = { api_key: setupApiKey.trim() };
        payload.authType = provider.authType;
      }

      const integration = await createIntegration(token, payload);

      if (provider.supportsOAuth) {
        // Redirect back to current page after OAuth completes
        const redirectUrl = window.location.href;
        const authUrl = await authorizeIntegration(token, integration.id, redirectUrl);
        window.open(authUrl, '_blank', 'noopener');
      } else {
        setSuccessMsg(`${provider.name} connected!`);
        setTimeout(() => setSuccessMsg(null), 3000);
        await loadIntegrations();
      }

      // Reset form
      setSetupName('');
      setSetupApiKey('');
      setSetupClientId('');
      setSetupClientSecret('');
      setExpandedId(null);
    } catch (err: any) {
      const message = err?.message || '';
      setError(message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncIntegration = async (id: string) => {
    if (!token) return;
    try {
      await syncIntegration(token, id);
      setSuccessMsg('Synced!');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch {
      setError('Sync failed.');
    }
    await loadIntegrations();
  };

  const handleReconnectIntegration = async (id: string, providerId: string) => {
    if (!token) return;
    try {
      // Redirect back to current page after OAuth completes
      const redirectUrl = window.location.href;
      const authUrl = await authorizeIntegration(token, id, redirectUrl);
      window.open(authUrl, '_blank', 'noopener');
    } catch {
      // Reconnect failed — delete the broken record and start fresh
      try {
        await deleteIntegration(token, id);
        const provider = providers.find(p => p.id === providerId);
        if (provider?.supportsOAuth && provider?.hasServerCredentials) {
          await handleConnectProvider(providerId, provider);
          return;
        }
      } catch {
        // Deletion also failed — show error
      }
      setError('Failed to reconnect. Try removing and re-adding the integration.');
    }
  };

  if (!isOpen) return null;

  const renderIntegrationItem = (item: UnifiedIntegration) => {
    const isExpanded = expandedId === item.id;
    const Logo = item.logo;
    const provider = providers.find(p => p.id === item.providerId);
    const isConnected = item.status === 'connected';
    const isAvailable = item.status === 'available';

    return (
      <div key={item.id} className={`integration-item ${item.status}`}>
        <div
          role="button"
          tabIndex={0}
          className="integration-item-header"
          onClick={() => {
            setExpandedId(isExpanded ? null : item.id);
            if (!isExpanded && item.status === 'available' && provider) {
              setSetupName(`My ${provider.name}`);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpandedId(isExpanded ? null : item.id);
              if (!isExpanded && item.status === 'available' && provider) {
                setSetupName(`My ${provider.name}`);
              }
            }
          }}
        >
          <div className="integration-item-logo">
            {Logo ? <Logo /> : <span className="integration-item-emoji">{provider?.icon || '🔌'}</span>}
          </div>
          <div className="integration-item-info">
            <div className="integration-item-name-row">
              <span className="integration-item-name">{item.name}</span>
              <span className={`integration-category-badge ${item.category}`}>
                {CATEGORY_LABELS[item.category]}
              </span>
            </div>
            <div className="integration-item-desc">{item.description}</div>
          </div>
          <div className="integration-action-buttons">
            {isConnected ? (
              // Show Remove button for connected integrations
              <button
                className="integration-action-btn remove"
                onClick={(e) => {
                  e.stopPropagation();
                  if ((item.providerId === 'google' || item.providerId === 'microsoft' || item.providerId === 'shopify') && item.isBuiltIn) {
                    setExpandedId(isExpanded ? null : item.id);
                  } else if (item.integration) {
                    handleDeleteIntegration(item.integration.id);
                  }
                }}
                title="Remove integration"
              >
                {(item.providerId === 'google' || item.providerId === 'microsoft' || item.providerId === 'shopify') && item.isBuiltIn ? 'Manage' : 'Remove'}
              </button>
            ) : (
              // Show Add button for available integrations
              <button
                className="integration-action-btn add"
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.providerId === 'google' && item.isBuiltIn) {
                    handleConnectGoogle();
                  } else if (item.providerId === 'microsoft' && item.isBuiltIn) {
                    handleConnectMicrosoft();
                  } else if (item.providerId === 'shopify' && item.isBuiltIn) {
                    setExpandedId(item.id);
                  } else if (provider) {
                    if (provider.supportsOAuth && provider.hasServerCredentials) {
                      handleConnectProvider(item.providerId, provider);
                    } else {
                      setExpandedId(item.id);
                    }
                  }
                }}
                title="Add integration"
              >
                Add
              </button>
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="integration-item-expanded">
            {/* Google expanded */}
            {item.providerId === 'google' && item.isBuiltIn && (
              <>
                {loadingGoogleAccounts ? (
                  <div className="integration-details">
                    <span>Loading accounts...</span>
                  </div>
                ) : (
                  <>
                    {googleAccounts.length > 0 ? (
                      <>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          padding: '12px 0'
                        }}>
                          {googleAccounts.map(account => (
                            <div
                              key={account.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '10px 12px',
                                borderRadius: '6px',
                                border: '1px solid var(--color-border)',
                                backgroundColor: 'var(--color-surface)',
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span
                                  style={{
                                    fontSize: '14px',
                                    color: 'var(--color-text)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={account.email}
                                >
                                  {account.email}
                                </span>
                                {account.syncErrorCode && (
                                  <IntegrationSyncStatusBadge
                                    syncErrorCode={account.syncErrorCode}
                                    syncError={account.syncError}
                                    syncErrorAt={account.syncErrorAt}
                                  />
                                )}
                              </div>
                              <button
                                onClick={() => handleDisconnectGoogleAccount(account.id)}
                                disabled={disconnecting}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: disconnecting ? 'not-allowed' : 'pointer',
                                  padding: '4px 8px',
                                  color: 'var(--color-text-subtle)',
                                  fontSize: '18px',
                                  lineHeight: '1',
                                  opacity: disconnecting ? 0.5 : 1,
                                  transition: 'transform 0.2s ease',
                                  flexShrink: 0,
                                }}
                                onMouseEnter={(e) => {
                                  if (!disconnecting) {
                                    e.currentTarget.style.transform = 'scale(1.2)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                                title="Disconnect this account"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="integration-actions">
                          <button
                            className="integration-btn secondary"
                            onClick={handleConnectGoogle}
                            style={{
                              width: '100%',
                              backgroundColor: 'var(--color-surface)',
                              padding: '10px 12px',
                              height: 'auto',
                              fontWeight: 'normal',
                              transition: 'font-weight 0.1s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.fontWeight = '600';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.fontWeight = 'normal';
                            }}
                          >
                            Add Account
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="integration-details">
                        <span>No Google accounts connected</span>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Microsoft expanded */}
            {item.providerId === 'microsoft' && item.isBuiltIn && (
              <>
                {msIntegration ? (
                  <div className="integration-actions" style={{ padding: '12px 0' }}>
                    <span style={{ fontSize: '14px', color: 'var(--color-text)' }}>
                      Connected — Outlook, Calendar, OneDrive, OneNote
                    </span>
                    <button
                      className="integration-btn secondary"
                      onClick={() => {
                        if (msIntegration) handleDeleteIntegration(msIntegration.id);
                      }}
                      style={{ marginTop: '8px' }}
                    >
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <div className="integration-actions" style={{ padding: '12px 0' }}>
                    <span style={{ fontSize: '14px', color: 'var(--color-text-subtle)' }}>
                      Not connected
                    </span>
                    <button
                      className="integration-btn secondary"
                      onClick={handleConnectMicrosoft}
                      style={{ marginTop: '8px', width: '100%' }}
                    >
                      Connect Microsoft 365
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Shopify expanded */}
            {item.providerId === 'shopify' && item.isBuiltIn && (
              <>
                {shopifyIntegration ? (
                  <div className="integration-actions" style={{ padding: '12px 0' }}>
                    <span style={{ fontSize: '14px', color: 'var(--color-text)' }}>
                      Connected — Orders, Products, Customers
                    </span>
                    <button
                      className="integration-btn secondary"
                      onClick={() => {
                        if (shopifyIntegration) handleDeleteIntegration(shopifyIntegration.id);
                      }}
                      style={{ marginTop: '8px' }}
                    >
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <div className="integration-setup" style={{ padding: '12px 0' }}>
                    <label className="setup-label">
                      Store Domain
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <input
                          type="text"
                          value={shopifyDomain}
                          onChange={e => setShopifyDomain(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && shopifyDomain.trim()) {
                              handleConnectShopify(shopifyDomain);
                            }
                          }}
                          placeholder="mystore"
                          className="integration-input"
                          style={{ flex: 1 }}
                        />
                        <span style={{ fontSize: '13px', color: 'var(--color-text-subtle)', whiteSpace: 'nowrap' }}>.myshopify.com</span>
                      </div>
                    </label>
                    <button
                      className="integration-btn secondary"
                      onClick={() => handleConnectShopify(shopifyDomain)}
                      disabled={!shopifyDomain.trim()}
                      style={{ marginTop: '8px', width: '100%' }}
                    >
                      Connect Shopify
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Generic connected integration */}
            {item.integration && item.status === 'connected' && (
              <div className="integration-actions">
                <button className="integration-btn secondary" onClick={() => handleSyncIntegration(item.integration!.id)}>
                  Sync
                </button>
              </div>
            )}

            {/* Available provider setup */}
            {item.status === 'available' && provider && (
              <div className="integration-setup">
                <label className="setup-label">
                  Name
                  <input
                    type="text"
                    value={setupName}
                    onChange={e => setSetupName(e.target.value)}
                    placeholder={`My ${provider.name}`}
                    className="integration-input"
                  />
                </label>
                {provider.supportsOAuth && !provider.hasServerCredentials ? (
                  // OAuth provider without server credentials - user must provide their own
                  <>
                    {provider.developerPortal ? (
                      <div className="setup-instructions">
                        <p className="setup-hint">
                          <a
                            href={provider.developerPortal.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="setup-portal-link"
                          >
                            Open {provider.name} Developer Portal →
                          </a>
                        </p>
                        <ol className="setup-steps">
                          {provider.developerPortal.instructions.map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ol>
                        {provider.developerPortal.formFields && (
                          <div className="setup-suggestions">
                            <span className="setup-suggestions-label">Suggested values:</span>
                            {provider.developerPortal.formFields.map((field, i) => (
                              <span key={i} className="setup-suggestion">
                                <strong>{field.name}:</strong> {field.value}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="setup-hint">
                        Create an OAuth app at {provider.name}'s developer portal, then enter your credentials below and press Enter.
                      </p>
                    )}
                    <label className="setup-label">
                      Client ID
                      <input
                        type="text"
                        value={setupClientId}
                        onChange={e => setSetupClientId(e.target.value)}
                        placeholder="Your OAuth Client ID"
                        className="integration-input"
                      />
                    </label>
                    <label className="setup-label">
                      Client Secret
                      <input
                        type="password"
                        value={setupClientSecret}
                        onChange={e => setSetupClientSecret(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && setupClientId.trim() && setupClientSecret.trim()) {
                            handleConnectProvider(item.providerId, provider);
                          }
                        }}
                        placeholder="Your OAuth Client Secret"
                        className="integration-input"
                      />
                    </label>
                  </>
                ) : provider.supportsOAuth && provider.hasServerCredentials ? (
                  // OAuth provider with server credentials - toggle handles connection
                  <p className="setup-hint">
                    Use the toggle above to authorize access to your {provider.name} account.
                  </p>
                ) : (
                  // API key provider
                  <>
                    <p className="setup-hint">
                      Enter your API key below and press Enter to save.
                    </p>
                    <label className="setup-label">
                      API Key
                      <input
                        type="password"
                        value={setupApiKey}
                        onChange={e => setSetupApiKey(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && setupApiKey.trim()) {
                            handleConnectProvider(item.providerId, provider);
                          }
                        }}
                        placeholder="sk_live_..."
                        className="integration-input"
                      />
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const modalContent = (
    <div className={`integrations-modal-overlay ${isMobile ? 'mobile' : ''}`} onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
      <div ref={modalContentRef} className={`integrations-modal ${isMobile ? 'mobile' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="integrations-modal-header">
          <h2>Add Integration</h2>
          <button className="integrations-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="integrations-modal-body xv-scroll">
          {/* Search box */}
          <div className="integrations-search">
            <svg className="integrations-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              className="integrations-search-input"
              placeholder="Search integrations..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Category tabs */}
          {availableCategories.length > 2 && (
            <div className="integrations-categories">
              {availableCategories.map(cat => (
                <button
                  key={cat}
                  className={`integrations-category-btn ${selectedCategory === cat ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {CATEGORY_LABELS[cat]}
                </button>
              ))}
            </div>
          )}

          {/* Messages */}
          {error && <p className="integration-error">{error}</p>}
          {successMsg && <p className="integration-success">{successMsg}</p>}

          {/* Integration list */}
          <div className="integrations-list">
            {filteredIntegrations.map(renderIntegrationItem)}
            {filteredIntegrations.length === 0 && (
              <p className="integrations-no-results">No integrations match your search.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (<>
    {createPortal(modalContent, document.body)}
    <SyncExplanationModal
      open={showSyncExplanationModal}
      onCancel={() => setShowSyncExplanationModal(false)}
      onConfirm={() => {
        setShowSyncExplanationModal(false);
        handleConnectGoogleConfirmed();
      }}
    />
  </>);
}
